import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "./button"

export interface EmptyStateProps {
  title: string
  description?: string
  icon?: LucideIcon
  /** `default` = neutral zero-state; `error` = destructive accent on icon. */
  variant?: 'default' | 'error'
  action?: {
    label: string
    onClick?: () => void
    href?: string
    variant?: 'primary' | 'tertiary'
  }
  className?: string
}

/**
 * Shared zero-state / error-state block.
 *
 * Visual pattern (Swiss design):
 *   - Centered vertical stack
 *   - Icon (optional, muted or destructive for errors)
 *   - Large title (`text-2xl`)
 *   - Small muted description
 *   - Optional action button
 */
export function EmptyState({ title, description, icon: Icon, variant = 'default', action, className }: EmptyStateProps) {
  const actionVariant = action?.variant ?? 'primary'
  const iconColor = variant === 'error' ? 'text-destructive' : 'text-muted-foreground'

  return (
    <div className={cn("flex flex-col items-center justify-center text-center gap-4 py-16 px-gutter", className)}>
      {Icon && <Icon className={cn('h-8 w-8', iconColor)} strokeWidth={1.5} />}
      <div>
        <h3 className="text-2xl font-medium text-foreground">{title}</h3>
        {description && (
          <p className="mt-2 max-w-md text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action && (
        action.href ? (
          <Button asChild variant={actionVariant}><a href={action.href}>{action.label}</a></Button>
        ) : (
          <Button variant={actionVariant} onClick={action.onClick}>{action.label}</Button>
        )
      )}
    </div>
  )
}
